<template>
  <d-container fluid class="main-content-container px-4">
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('marketing.salesTools') }}</span>
        <h3 class="page-title">{{ $t('marketing.newSegment') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/funkcje-sprzedazowe/segmenty')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('marketing.addSegment') }}
        </d-button>
      </d-col>
    </d-row>

    <d-alert
      v-if="loadError"
      show
      theme="warning">
      {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
    </d-alert>

    <!-- Form -->
    <d-form
      v-else
      :class="{ 'is-loading': !allDataLoaded }">
      <validation-observer
        ref="segment-new-form"
        tag="form"
        v-slot="{
          errors: formErrors,
          valid: isFormValid,
          validated: isFormValidated
        }">
        <d-card class="mb-5">
          <d-tabs v-sticky-tabs
            card
            :nav-class="allDataLoaded ? validationResults : ''">

            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('ui.generalInfo') }}
              </h4>

              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('ui.name') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="data.name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label for="customers_order_by">
                      {{ $t('marketing.customersOrderBy') }}:
                    </label>

                    <validation-provider
                      name="customers_order_by"
                      v-slot="{ errors }">
                      <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                        <v-select
                          id="customers_order_by"
                          :options="customersOrderByOptions"
                          v-model="data.customers_order_by"
                          :searchable="false"
                          :clearable="true"
                          :reduce="item => item.value"
                          :placeholder="$t('ui.chooseOption')"
                          label="text">
                          <template slot="no-options">
                            {{ $t('ui.noOptionsAvailable') }}&hellip;
                          </template>
                        </v-select>
                      </div>

                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label for="customers_limit">
                      {{ $t('marketing.maxCustomers') }}:
                    </label>

                    <validation-provider
                      rules="required|only-integer-numbers|min_value:1"
                      name="customers_limit"
                      v-slot="{ errors }">
                      <d-input
                        v-model="data.customers_limit"
                        :class="{
                          'form-control': true,
                          'is-invalid': errors.length
                        }"
                        :invalid="errors.length ? true : null"
                        type="number"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="12">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.description') }}:
                    </label>

                    <d-textarea
                      :value.prop="data.description"
                      v-model="data.description" />
                  </div>
                </d-col>
              </d-row>
            </d-tab>

            <!-- Filtry -->
            <d-tab :title="$t('marketing.segmentFilters')">
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('marketing.segmentFilters') }}
              </h4>

              <validation-provider
                ref="filtersLengthValidator"
                rules="required|min:1"
                name="filters"
                v-slot="{ errors }">
                <input
                  :class="{
                    'form-control': true,
                    'is-invalid': errors.length
                  }"
                  :value="data.filters"
                  disabled
                  type="hidden"
                  :state="errors.length ? 'invalid' : null" />
                <d-form-invalid-feedback class="mb-2">
                  {{ $t('marketing.atLeastOneFilterRequired') }}
                </d-form-invalid-feedback>
              </validation-provider>

              <div
                class="filters-container pt-3 pl-3 pr-3 pb-1 bg-light rounded mb-3"
                :class="{ 'border-danger': formErrors.filters && formErrors.filters.length > 0 }">

                <div class="filter-group">
                  <h6 class="mt-0 mb-3 text-primary">
                    {{ $t('customer.customers') }}
                  </h6>
                  <segment-filter
                    v-for="customerFilter in segmentsConfig.customers"
                    :key="'filters-customers-' + customerFilter.field"
                    :filter-config="customerFilter"
                    :filter-group="'customers'"
                    :forceRequiredFilter="forceRequiredFilter"
                    @filter-changed="updateFilters" />
                </div>
              </div>

              <div
                class="filters-container pt-3 pl-3 pr-3 pb-1 bg-light rounded mb-3"
                :class="{ 'border-danger': formErrors.filters && formErrors.filters.length > 0 }">

                <div class="filter-group">
                  <h6 class="mt-0 mb-3 text-primary">
                    {{ $t('customer.orders') }}
                  </h6>
                  <segment-filter
                    v-for="orderFilter in segmentsConfig.orders"
                    :key="'filters-orders-' + orderFilter.field"
                    :filter-config="orderFilter"
                    :filter-group="'orders'"
                    :forceRequiredFilter="forceRequiredFilter"
                    @filter-changed="updateFilters" />
                </div>
              </div>

              <div
                class="filters-container pt-3 pl-3 pr-3 pb-1 bg-light rounded mb-3"
                :class="{ 'border-danger': formErrors.filters && formErrors.filters.length > 0 }">

                <div class="filter-group">
                  <h6 class="mt-0 mb-3 text-primary">
                    {{ $t('product.products') }}
                  </h6>
                  <segment-filter
                    v-for="productFilter in segmentsConfig.products"
                    :key="'filters-products-' + productFilter.field"
                    :filter-config="productFilter"
                    :filter-group="'products'"
                    :forceRequiredFilter="forceRequiredFilter"
                    @filter-changed="updateFilters" />
                </div>
              </div>
            </d-tab>

            <d-tab :title="$t('marketing.resultsPreview')">
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('marketing.resultsPreview') }}
              </h4>
              <segment-results
                :form-errors="formErrors"
                :is-form-invalid="!isFormValid && isFormValidated"
                :is-preview="true"
                :segment-data="data"
                @validate-before-preview="validateForPreview" />
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded && !loadError" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>
        </d-card>
      </validation-observer>
    </d-form>
  </d-container>
</template>

<script>
import FormUtils from '@/utils/FormUtils.js';
import SegmentFilter from './SegmentFilter.vue';
import SegmentResults from './SegmentResults.vue';

export default {
  name: 'marketing-segment-new',
  components: {
    'segment-filter': SegmentFilter,
    'segment-results': SegmentResults
  },
  computed: {
    allDataLoaded () {
      return this.loadedSegmentsConfig;
    },
    validationResults () {
      if (!this.$refs['segment-new-form']) {
        return '';
      }

      let errors = this.$refs['segment-new-form'].errors;
      let errorTabs = [];

      // Sprawdzamy błędy w pierwszej zakładce (Dane ogólne)
      let tab1Fields = ['name', 'customers_order_by', 'customers_limit'];
      for (let i = 0; i < tab1Fields.length; i++) {
        if (errors[tab1Fields[i]] && errors[tab1Fields[i]].length) {
          errorTabs.push('error-1');
          break;
        }
      }

      // Sprawdzamy błędy w drugiej zakładce (Filtry) - czyli takie, które nie są z pierwszej zakładki
      let hasFilterErrors = Object.keys(errors).some(key => {
        if (tab1Fields.includes(key)) {
          return false;
        }
        return errors[key] && errors[key].length > 0;
      });

      if (hasFilterErrors) {
        errorTabs.push('error-2');
      }

      return errorTabs.join(' ');
    },
    forceRequiredFilter () {
      if (this.data.customers_order_by === 'orders_count') {
        return {
          group: 'orders',
          field: 'orders_count'
        }
      }

      if (this.data.customers_order_by === 'orders_amount') {
        return {
          group: 'orders',
          field: 'orders_amount'
        }
      }

      return null;
    }
  },
  data () {
    return {
      data: {
        customers_limit: 1,
        customers_order_by: null,
        description: null,
        filters: [],
        name: ''
      },
      segmentsConfig: {},
      loadError: false,
      loadedSegmentsConfig: false,
      customersOrderByOptions: [
        {
          value: 'orders_count',
          text: this.$t('marketing.ordersCount')
        },
        {
          value: 'orders_amount',
          text: this.$t('marketing.ordersAmount')
        }
      ]
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/marketing/segments/config',
        loadedKey: 'loadedSegmentsConfig',
        outputKey: 'segmentsConfig',
        errorKey: 'loadError',
      });
    },
    updateFilters (payload) {
      const existingIndex = this.data.filters.findIndex(item => item.field === payload.field && item.group === payload.group);

      if (payload.isActive) {
        const filterData = {
          field: payload.field,
          group: payload.group,
          name: payload.name,
          type: payload.type,
          value: payload.value
        };

        if (existingIndex > -1) {
          this.$set(this.data.filters, existingIndex, filterData);
        } else {
          this.data.filters.push(filterData);
        }
      } else if (existingIndex > -1) {
        this.data.filters.splice(existingIndex, 1);
      }

      this.$nextTick(() => {
        if (this.$refs['filtersLengthValidator'] && this.$refs['segment-new-form'].flags.validated) {
          const validationValue = this.data.filters.length > 0 ? 'valid' : '';
          this.$refs['filtersLengthValidator'].validate(validationValue);
        }
      });
    },
    validateForPreview (proceedCallback) {
      FormUtils.validate(this.$refs['segment-new-form'], proceedCallback);
    },
    validateForm () {
      FormUtils.validate(this.$refs['segment-new-form'], this.submitForm);
    },
    submitForm () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/marketing/segments/store',
        formData: this.data,
        successTitle: this.$t('marketing.addSegmentSuccessTitle'),
        successText: this.$t('marketing.addSegmentSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('marketing.addSegmentErrorText'),
        successAction: (response) => {
          this.$router.push('/funkcje-sprzedazowe/segmenty/edycja/' + parseInt(response.id, 10));
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
form.is-loading {
  opacity: .25;
  pointer-events: none;
}

.filters-container {
  border: 1px solid #e1e5eb;
  transition: border-color 0.2s ease-in-out;

  &.border-danger {
    border-color: #c4183c !important;
  }
}
</style>
